// views/Dashboard/PainelExecutivo/areas/visaoGeral/BlocoResumo.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ArrowDownRight, Loader2 } from 'lucide-react';
import { variacaoPct } from '../../../../../src/lib/dashboard/periodoAnterior';
import { caminhoDaArea } from '../../types';
import type { AreaPainel } from '../../types';

export interface Metrica {
  titulo: string;
  valor: string;
  /** Valores crus, para a variação. Sem `anterior`, a variação não aparece. */
  bruto?: number;
  anterior?: number | null;
  /** `false` quando crescer é RUIM (inadimplência, custo por resultado). */
  crescerEhBom?: boolean;
  detalhe?: string;
}

interface Props {
  titulo: string;
  /** Área de destino. O link sai absoluto — ver CAMINHO_PAINEL em `types.ts`. */
  para: AreaPainel;
  metricas: Metrica[];
  loading: boolean;
  erro?: string | null;
  /** Rótulo do período anterior, para o tooltip da variação. */
  rotuloAnterior: string | null;
}

const fmtPct = (v: number) => `${v > 0 ? '+' : ''}${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/**
 * Um bloco da Visão geral: o nome da área, os números do período e o caminho
 * para os detalhes. Cada bloco carrega sozinho (ver VisaoGeral.tsx) — o
 * primeiro pronto já aparece, em vez de a tela inteira esperar a base mais lenta.
 */
const BlocoResumo: React.FC<Props> = ({ titulo, para, metricas, loading, erro, rotuloAnterior }) => (
  <section className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
    <Link
      to={caminhoDaArea(para)}
      className="group flex items-center justify-between gap-3 px-5 pt-4 pb-3 hover:text-primary"
      aria-label={`Abrir a área ${titulo}`}
    >
      <h2 className="text-sm font-bold text-gray-800 dark:text-white group-hover:text-primary transition-colors">{titulo}</h2>
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 group-hover:text-primary transition-colors">
        Ver detalhes <ArrowRight size={14} />
      </span>
    </Link>

    <div className="px-5 pb-5">
      {erro ? (
        <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>
      ) : loading ? (
        <p className="flex items-center gap-2 py-3 text-sm text-gray-400">
          <Loader2 size={15} className="animate-spin" /> Carregando…
        </p>
      ) : (
        // Uma métrica por linha no celular — no print do usuário, duas colunas
        // cortavam o título e o valor no meio.
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {metricas.map((m) => {
            const v = m.bruto != null && m.anterior != null ? variacaoPct(m.bruto, m.anterior) : null;
            const bom = m.crescerEhBom === false ? (v ?? 0) < 0 : (v ?? 0) > 0;
            return (
              <div key={m.titulo} className="rounded-xl bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
                  {m.titulo}
                </p>
                <p className="mt-0.5 text-lg font-bold tabular-nums text-gray-900 dark:text-white truncate">{m.valor}</p>
                {m.detalhe && <p className="text-[11px] text-gray-400 truncate">{m.detalhe}</p>}
                {v !== null && (
                  <p
                    title={rotuloAnterior ? `Contra ${rotuloAnterior}` : undefined}
                    className={`mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-semibold ${
                      bom ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {v > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {fmtPct(v)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  </section>
);

export default BlocoResumo;
